import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';

export enum VisitorGroupOrigin {
  Preset = 'PRESET',
  Atlas = 'ATLAS',
  EntraId = 'ENTRA_ID',
  Unknown = 'UNKNOWN'
}

const HIDDEN_VISITOR_GROUP_TYPES = [VisitorGroupType.AtlasInternal, VisitorGroupType.ExternalVisitors] as const;

export const isHiddenVisitorGroupType = (type: string): boolean =>
  (HIDDEN_VISITOR_GROUP_TYPES as readonly string[]).includes(type);

export const filterHiddenVisitorGroups = <T extends Pick<VisitorGroup, 'type'>>(visitorGroups: T[]): T[] =>
  visitorGroups.filter((group) => !isHiddenVisitorGroupType(group.type));

export const getVisitorGroupOrigin = (visitorGroup: Pick<VisitorGroup, 'type'>): VisitorGroupOrigin => {
  switch (visitorGroup.type) {
    case VisitorGroupType.Administrators:
    case VisitorGroupType.Installers:
      return VisitorGroupOrigin.Preset;
    case VisitorGroupType.CoContractors:
      return VisitorGroupOrigin.Atlas;
    case VisitorGroupType.AzureAd:
      return VisitorGroupOrigin.EntraId;
    default:
      return VisitorGroupOrigin.Unknown;
  }
};

export const getVisitorGroupOriginLabel = (
  visitorGroup: Pick<VisitorGroup, 'type'>,
  translation: Translation
): string => {
  switch (getVisitorGroupOrigin(visitorGroup)) {
    case VisitorGroupOrigin.Preset:
      return translation.preset;
    case VisitorGroupOrigin.Atlas:
      return translation.atlas;
    case VisitorGroupOrigin.EntraId:
      return translation.entra_id;
    default:
      return translation.unknown;
  }
};

export const canEditVisitorGroup = (visitorGroup: Pick<VisitorGroup, 'type'>): boolean =>
  getVisitorGroupOrigin(visitorGroup) === VisitorGroupOrigin.Atlas;

export const canDeleteVisitorGroup = (visitorGroup: Pick<VisitorGroup, 'type'>): boolean =>
  getVisitorGroupOrigin(visitorGroup) === VisitorGroupOrigin.Atlas;

export const canShareKeysInVisitorGroup = (visitorGroup: Pick<VisitorGroup, 'type'>): boolean =>
  getVisitorGroupOrigin(visitorGroup) === VisitorGroupOrigin.Atlas;

export const canManageUsersInVisitorGroup = (visitorGroup: Pick<VisitorGroup, 'type'>): boolean => {
  const origin = getVisitorGroupOrigin(visitorGroup);
  return origin === VisitorGroupOrigin.Preset || origin === VisitorGroupOrigin.Atlas;
};
